import { useEffect, useMemo, useState } from 'react';
import { useMount } from 'react-use';
import { SliderClasses } from '@mui/material/Slider';

import useEvent from '@lyearn/core/hooks/useEvent';
import { useTranslation } from '@lyearn/i18n';
import { cn, css } from '@lyearn/style';
import { Box, Slider } from '@lyearn/ui';
import { Maybe, RatingLabelComponent } from '@/types/schema';

import SliderThumb from './SliderThumb';
import { getRenderedSliderMarks } from './utils';

interface LikertScaleProps {
  className?: string;
  value?: Maybe<number>;
  onChange: (val: number) => void;
  size?: 'small' | 'large' | 'large-responsive';
  ratingLabels?: RatingLabelComponent[];
  classes?: {
    slider?: Partial<SliderClasses>;
  };
}

const SLIDER_CLASSSES_CONFIG: Record<
  'small' | 'large' | 'large-responsive',
  Partial<SliderClasses>
> = {
  small: {
    track: 'h-6 bg-interactive-primary-active border-none',
    rail: 'h-6 bg-bg-tertiary opacity-100',
    markLabel: 'w-44 top-[3rem]',
    mark: 'opacity-0',
    thumb: 'w-28 h-28 bg-white border border-link-white-hover !shadow-none',
  },
  large: {
    track: 'h-8 bg-interactive-primary-active border-none',
    rail: 'h-8 bg-bg-tertiary opacity-100',
    markLabel: 'w-60 top-[4.2rem]',
    mark: 'opacity-0',
    thumb: 'w-36 h-36 bg-white border border-link-white-hover !shadow-none',
  },
  'large-responsive': {
    track: 'h-8 bg-interactive-primary-active border-none',
    rail: 'h-8 bg-bg-tertiary opacity-100',
    markLabel: cn(
      'w-[18%] top-[4.2rem]',
      css`
        &[data-index='0'] {
          transform: translateX(-40%);
        }
        &[data-index='1'] {
          transform: translateX(-45%);
        }
        &[data-index='2'] {
          transform: translateX(-50%);
        }
        &[data-index='3'] {
          transform: translateX(-55%);
        }
        &[data-index='4'] {
          transform: translateX(-60%);
        }
      `,
    ),
    mark: 'opacity-0',
    thumb: 'w-36 h-36 bg-white border border-link-white-hover !shadow-none',
  },
};

const CONTAINER_CLASSES_CONFIG: Record<'small' | 'large' | 'large-responsive', string> = {
  small: 'mx-26 mb-26',
  large: 'mx-26 mb-26',
  'large-responsive': 'mx-26 mb-40', // TODO: bottom margin should be based on the height of labels, to make sure labels do not overflow
};

const DEFAULT_VALUE = 3;
const COMPONENTS = { thumb: SliderThumb };

const LikertScale: React.FC<LikertScaleProps> = (props) => {
  const { t } = useTranslation();
  const { className, value, onChange: onChangeProp, size = 'small', ratingLabels } = props;
  const marks = getRenderedSliderMarks(t, ratingLabels);

  const SLIDER_CLASSES = useMemo(() => SLIDER_CLASSSES_CONFIG[size], [size]);

  // Drive the thumb from local state while dragging so every pointer-move tick
  // only re-renders this component. Propagating up (onChangeProp) on every tick
  // was making the whole response-state tree re-render mid-drag, which delayed
  // the next paint and made the thumb visibly lag behind the pointer.
  const [localValue, setLocalValue] = useState(value ?? DEFAULT_VALUE);

  useEffect(() => {
    if (typeof value === 'number') {
      setLocalValue(value);
    }
  }, [value]);

  const onDrag = useEvent((_: any, val: number | number[]) => {
    if (Array.isArray(val)) {
      return;
    }
    setLocalValue(val);
  });

  const onDragCommitted = useEvent((_: any, val: number | number[]) => {
    if (Array.isArray(val)) {
      return;
    }
    onChangeProp(val);
  });

  useMount(() => {
    if (typeof value !== 'number') {
      onChangeProp(DEFAULT_VALUE);
    }
  });

  return (
    <Box className={CONTAINER_CLASSES_CONFIG[size]}>
      <Slider
        className={className}
        classes={SLIDER_CLASSES}
        marks={marks}
        max={5}
        min={1}
        slots={COMPONENTS}
        value={localValue}
        onChange={onDrag}
        onChangeCommitted={onDragCommitted}
      />
    </Box>
  );
};

export default LikertScale;
